// Print templates inventory — Settings → Print Templates.
//
// Shows one card per template_type. Staff can read; admins can edit
// or revert. The Edit button always navigates to the builder; the
// "Revert to default" button is only rendered for admins on
// customised rows and triggers a confirm modal before the soft-delete.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Printer, RotateCcw } from 'lucide-react';

import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Badge } from '../../../components/ui/Badge/Badge';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { useAuthStore } from '../../../store/authStore';
import { extractErrorMessage } from '../../../utils/errorMessage';
import {
  usePrintTemplatesList,
  useRevertPrintTemplate,
} from './hooks/usePrintTemplates';
import type { PrintTemplateInfo, PrintTemplateType } from '../../../types/api';
import styles from './PrintTemplatesListPage.module.css';

const TYPE_LABELS: Record<PrintTemplateType, string> = {
  diary_results: 'Diary Results',
  receipt: 'Receipt',
  invoice: 'Invoice',
  wb_ticket: 'Weighbridge Ticket',
  wb_advice_note: 'Advice Note',
  wb_wtn: 'Waste Transfer Note',
  wb_hazardous_consignment: 'Hazardous Consignment Note',
};

const TYPE_DESCRIPTIONS: Record<PrintTemplateType, string> = {
  diary_results: 'Used by the diary page Print and Download PDF actions.',
  receipt: 'Stub — reuse arrives once receipts migrate onto the new system.',
  invoice: 'Stub — reuse arrives once invoices migrate onto the new system.',
  wb_ticket: 'Headline ticket PDF rendered from each completed weighbridge job.',
  wb_advice_note:
    'Internal advice / dispatch note emailed to the customer at ticket completion.',
  wb_wtn:
    'Waste Transfer Note — produced when WTN compliance is enabled and the ticket carries waste lines.',
  wb_hazardous_consignment:
    'Hazardous Consignment Note — produced when hazardous compliance is enabled and the ticket carries hazardous lines.',
};

export function PrintTemplatesListPage(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';

  const list = usePrintTemplatesList();
  const revert = useRevertPrintTemplate();

  const [revertTarget, setRevertTarget] = useState<PrintTemplateInfo | null>(null);
  const [revertError, setRevertError] = useState<string | null>(null);

  function openRevertConfirm(tpl: PrintTemplateInfo): void {
    setRevertError(null);
    setRevertTarget(tpl);
  }

  function closeRevertConfirm(): void {
    setRevertError(null);
    setRevertTarget(null);
  }

  function handleRevertConfirm(): Promise<void> {
    if (revertTarget === null) return Promise.resolve();
    setRevertError(null);
    return revert
      .mutateAsync(revertTarget.template_type)
      .then(() => {
        setRevertTarget(null);
      })
      .catch((err: unknown) => {
        // Keep the confirm modal open and show why the revert failed —
        // silently swallowing it here left the modal sitting open with
        // no explanation.
        setRevertError(extractErrorMessage(err));
      });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Print Templates"
        subtitle="Customise the layout of every printable document this app produces."
      />

      {list.isLoading ? (
        <Card>
          <div className={styles.loading}>
            <Spinner label="Loading templates" />
          </div>
        </Card>
      ) : list.isError ? (
        <Card title="Could not load templates">
          <p className={styles.errorText}>
            We hit a problem fetching the print templates. Try again in a
            moment.
          </p>
          <Button variant="primary" onClick={() => list.refetch()}>
            Retry
          </Button>
        </Card>
      ) : (
        <ul className={styles.grid}>
          {(list.data?.templates ?? []).map((tpl) => {
            const label = TYPE_LABELS[tpl.template_type];
            const description = TYPE_DESCRIPTIONS[tpl.template_type];
            const editPath = `/settings/print-templates/${tpl.template_type}/edit`;
            return (
              <li key={tpl.template_type}>
                <Card>
                  <div className={styles.cardRow}>
                    <span className={styles.iconWrap}>
                      <Printer size={20} aria-hidden />
                    </span>
                    <div className={styles.cardText}>
                      <div className={styles.titleRow}>
                        <h3 className={styles.title}>{label}</h3>
                        {tpl.is_custom ? (
                          <Badge variant="info">Custom</Badge>
                        ) : (
                          <Badge variant="muted">Default</Badge>
                        )}
                      </div>
                      <p className={styles.description}>{description}</p>
                      <p className={styles.meta}>
                        <span className={styles.metaLabel}>Name:</span>{' '}
                        {tpl.template_name}
                      </p>
                    </div>
                    <div className={styles.cardActions}>
                      <Link to={editPath} className={styles.editLink}>
                        <Button variant="primary" icon={Pencil}>
                          Edit Template
                        </Button>
                      </Link>
                      {isAdmin && tpl.is_custom ? (
                        <Button
                          variant="danger"
                          icon={RotateCcw}
                          onClick={() => openRevertConfirm(tpl)}
                        >
                          Revert to default
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmModal
        open={revertTarget !== null}
        title={
          revertTarget !== null
            ? `Revert ${TYPE_LABELS[revertTarget.template_type]} template?`
            : 'Revert template?'
        }
        message={
          <>
            <p>
              This will permanently remove your custom template and restore
              the system default. This cannot be undone.
            </p>
            {revertError !== null && (
              <p className={styles.revertError}>{revertError}</p>
            )}
          </>
        }
        confirmLabel="Revert to default"
        cancelLabel="Cancel"
        variant="danger"
        loading={revert.isPending}
        onConfirm={handleRevertConfirm}
        onCancel={closeRevertConfirm}
      />
    </div>
  );
}
